import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import {
  getApiUrl,
  getTokenGetter,
  waitForAuthFunctions,
  useGetAWSAccountsQuery,
  useGetDNSSetupQuery,
  useUpdateDMARCMutation,
  useValidateDNSRecordsMutation
} from '../services/api';

const getEnvName = () => {
  if (typeof window === 'undefined') return 'unknown';
  const hostname = window.location.hostname || '';
  if (hostname.includes('staging')) return 'staging';
  if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') return 'production';
  return 'unknown';
};

// Main KG AWS account (Route53). Root/current account is NOT in AWS_ACCOUNTS_TABLE by design.
// When domain has no stored accountId and is AWS-hosted, default to this so Apply works without requiring dropdown selection.
const MAIN_AWS_ACCOUNT_ID = '982423662433';
const MAILCHANNELS_DEFAULT_POOL_ID = 'mailchannels_primary';

const DMARC_PRESETS = [
  { value: 'none', label: 'Monitor only (p=none)' },
  { value: 'quarantine', label: 'Quarantine (p=quarantine)' },
  { value: 'reject', label: 'Reject (p=reject)' }
];

const SPF_PROVIDERS = [
  { value: 'smtp_com', label: 'SMTP.com', include: 'include:_spf.smtp.com' },
  { value: 'mailchannels', label: 'MailChannels', include: 'include:relay.mailchannels.net' },
  { value: 'mailgun', label: 'Mailgun', include: 'include:mailgun.org' },
  { value: 'sendgrid', label: 'Twilio SendGrid', include: 'include:sendgrid.net' },
  { value: 'elastic_email', label: 'Elastic Email', include: 'include:_spf.elasticemail.com' },
  { value: 'kg_host', label: 'KG Host', include: 'ip4:YOUR_KG_HOST_IP' },
  { value: 'kg_rack', label: 'KG Rack', include: 'ip4:YOUR_KG_RACK_IP' },
  { value: 'custom', label: 'Custom (advanced)', include: '' },
  { value: 'keep', label: 'Keep existing SPF (no change)', include: '' }
];

function buildDmarcValue({ domain, policy, ruaEmail }) {
  const pctPart = policy === 'none' ? '' : '; pct=100';
  return `v=DMARC1; p=${policy}; adkim=r; aspf=r${pctPart}; rua=mailto:${ruaEmail}`;
}

function buildSpfValue({ providerValue, existingValue, customValue }) {
  if (providerValue === 'keep') return existingValue || '';
  if (providerValue === 'custom') {
    const v = String(customValue || '').trim();
    if (!v) return existingValue || '';
    if (v.toLowerCase().startsWith('v=spf1')) return v;
    return `v=spf1 ${v} ~all`;
  }
  const provider = SPF_PROVIDERS.find((p) => p.value === providerValue);
  if (!provider?.include) return existingValue || '';
  return `v=spf1 ${provider.include} ~all`;
}

function normalizeDkimTxtValue(v) {
  const s = String(v || '').trim();
  if (!s) return '';
  // Accept either full "k=rsa; p=..." or just the public key value.
  if (s.toLowerCase().includes('k=rsa') && s.toLowerCase().includes('p=')) return s;
  // If they pasted only the key, wrap it.
  if (/^[A-Za-z0-9+/=]+$/.test(s) && s.length > 50) {
    return `k=rsa; p=${s}`;
  }
  return s;
}

function normalizeMailgunSelectorInput(selector, domain) {
  let s = String(selector || '').trim();
  if (!s) return '';
  s = s.replace(/^"+|"+$/g, '').trim().replace(/\.$/, '');
  const d = String(domain || '').trim().replace(/\.$/, '').toLowerCase();
  const lower = s.toLowerCase();
  const suffixFull = `._domainkey.${d}`;
  if (d && lower.endsWith(suffixFull)) s = s.slice(0, s.length - suffixFull.length);
  if (s.toLowerCase().endsWith('._domainkey')) s = s.slice(0, -'._domainkey'.length);
  if (s.includes('.')) s = s.split('.')[0];
  return s.trim();
}

function isLikelyEmailAddress(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s);
}

function getFriendlyMailchannelsDkimGenerateError(rawMessage) {
  const msg = String(rawMessage || '').trim();
  if (!msg) return 'Could not generate MailChannels DKIM right now. Try again in a few seconds.';
  if (/api key is not configured/i.test(msg) || /MAILCHANNELS_API_KEY/i.test(msg) || /\/kg\/send-pools\//i.test(msg)) {
    return 'Connect MailChannels to enable automatic DKIM generation.';
  }
  if (/invalid api key/i.test(msg) || /unauthorized/i.test(msg) || /forbidden/i.test(msg) || /authentication/i.test(msg)) {
    return 'MailChannels API key is invalid. Paste a valid API key and try again.';
  }
  if (/selector is required/i.test(msg)) {
    return 'KG now auto-generates the DKIM selector. Click Generate again.';
  }
  return msg;
}

function isMailchannelsApiKeyMissingSignal(code, messageKey, rawMessage) {
  const c = String(code || '').trim().toUpperCase();
  const mk = String(messageKey || '').trim().toLowerCase();
  const msg = String(rawMessage || '').trim();
  if (c === 'MAILCHANNELS_DKIM_AUTOGEN_NOT_CONFIGURED' || c === 'MISSING_API_KEY') return true;
  if (mk === 'mailchannels_missing_api_key') return true;
  return /automatic dkim generation is not enabled/i.test(msg)
    || /api key is not configured/i.test(msg)
    || /MAILCHANNELS_API_KEY/i.test(msg)
    || /\/kg\/send-pools\//i.test(msg);
}

function isMailchannelsApiKeyInvalidSignal(code, messageKey, rawMessage) {
  const c = String(code || '').trim().toUpperCase();
  const mk = String(messageKey || '').trim().toLowerCase();
  const msg = String(rawMessage || '').trim();
  if (c === 'INVALID_API_KEY') return true;
  if (mk === 'mailchannels_invalid_api_key') return true;
  return /invalid api key/i.test(msg)
    || /unauthorized/i.test(msg)
    || /forbidden/i.test(msg)
    || /authentication/i.test(msg);
}

function getFriendlyMailchannelsConnectError(code, messageKey, rawMessage) {
  const msg = String(rawMessage || '').trim();
  if (isMailchannelsApiKeyInvalidSignal(code, messageKey, msg)) {
    return 'MailChannels API key is invalid. Check it and try again.';
  }
  if (!msg) return 'Could not connect MailChannels right now. Please try again.';
  return msg;
}

function flattenDnsSetupRecords(dnsRecords) {
  if (!dnsRecords) return [];
  const out = [];
  // Do not include SES verification; this modal is DNS-only (SPF/DKIM/DMARC).
  if (dnsRecords.spf) out.push(dnsRecords.spf);
  if (Array.isArray(dnsRecords.dkim)) out.push(...dnsRecords.dkim);
  if (dnsRecords.dmarc) out.push(dnsRecords.dmarc);
  // Intentionally exclude MX records from this flow (inbound is Postmark-only).
  return out;
}

function formatRecordLine(r) {
  if (!r) return '';
  return `${r.name}  ${r.type}  ${r.value}`;
}

export default function EmailAuthWizardModal({ show, onHide, domainObj, onAuthSuccess, tenantDmarcEmail }) {
  const envName = getEnvName();
  const isStaging = envName === 'staging';

  const domain = domainObj?.domain || '';
  const initialAccountId = domainObj?.accountId || '';
  const [boundAccountId, setBoundAccountId] = useState(initialAccountId);

  const [step, setStep] = useState('plan'); // plan | apply | verify
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [dmarcEmail, setDmarcEmail] = useState('');
  const [dmarcPolicy, setDmarcPolicy] = useState(isStaging ? 'none' : 'none');
  const [spfProvider, setSpfProvider] = useState('smtp_com');
  const [customSpfValue, setCustomSpfValue] = useState('');
  const [smtpComDkimTxtValue, setSmtpComDkimTxtValue] = useState('');
  const [skipDkim, setSkipDkim] = useState(false);
  const [mailchannelsAuthId, setMailchannelsAuthId] = useState('');
  const [mailchannelsDkimSelector, setMailchannelsDkimSelector] = useState('');
  const [mailchannelsDkimTxtValue, setMailchannelsDkimTxtValue] = useState('');
  const [mailgunDkimSelector, setMailgunDkimSelector] = useState('');
  const [mailgunDkimTxtValue, setMailgunDkimTxtValue] = useState('');
  const [elasticEmailDkimSelector, setElasticEmailDkimSelector] = useState('api');
  const [elasticEmailDkimTxtValue, setElasticEmailDkimTxtValue] = useState('');

  const [isApplying, setIsApplying] = useState(false);
  const [isGeneratingMailchannelsDkim, setIsGeneratingMailchannelsDkim] = useState(false);
  const [mailchannelsDkimInlineStatus, setMailchannelsDkimInlineStatus] = useState(null); // { variant: 'success' | 'danger' | 'secondary', message: string }
  const [showMailchannelsApiKeyConnect, setShowMailchannelsApiKeyConnect] = useState(false);
  const [mailchannelsApiKeyDraft, setMailchannelsApiKeyDraft] = useState('');
  const [isSavingMailchannelsApiKey, setIsSavingMailchannelsApiKey] = useState(false);
  const [existingRecords, setExistingRecords] = useState(null);
  const [existingRecordsChoice, setExistingRecordsChoice] = useState('keep'); // keep | delete
  const [verifyDetails, setVerifyDetails] = useState(null);

  const { data: awsAccountsResp } = useGetAWSAccountsQuery(undefined, { skip: !show });
  const awsAccounts = awsAccountsResp?.accounts || [];

  const { data: dnsSetupResp, isLoading: isDnsSetupLoading, error: dnsSetupError, refetch: refetchDnsSetup } =
    useGetDNSSetupQuery(domain, { skip: !show || !domain });

  const [updateDMARC, { isLoading: isUpdatingDmarc }] = useUpdateDMARCMutation();
  const [validateDNSRecords, { isLoading: isValidating }] = useValidateDNSRecordsMutation();

  useEffect(() => {
    if (!show) return;
    setStep('plan');
    setError(null);
    setInfo(null);
    setExistingRecords(null);
    setExistingRecordsChoice('keep');
    setVerifyDetails(null);
    setMailchannelsDkimInlineStatus(null);
    setShowMailchannelsApiKeyConnect(false);
    setMailchannelsApiKeyDraft('');
    setIsSavingMailchannelsApiKey(false);
    setDmarcPolicy(isStaging ? 'none' : 'none');
    const tenantDefault = String(tenantDmarcEmail || '').trim();
    setDmarcEmail(tenantDefault || (domain ? `dmarc@${domain}` : ''));
    setBoundAccountId(domainObj?.accountId || '');
    // Pre-fill SPF/DKIM from domain when opening so user can view/update (e.g. Mailgun selector)
    const providerFromDomain = String(domainObj?.spfProvider || domainObj?.authProvider || '').toLowerCase();
    const hasMailgun = domainObj?.mailgunDkimSelector || providerFromDomain === 'mailgun';
    const hasMailchannels = !hasMailgun && (domainObj?.mailchannelsDkimSelector || providerFromDomain === 'mailchannels');
    const hasSendgrid = !hasMailgun && !hasMailchannels && providerFromDomain === 'sendgrid';
    const hasElasticEmail = !hasMailgun && !hasMailchannels && !hasSendgrid && (providerFromDomain === 'elastic_email' || providerFromDomain === 'elasticemail' || domainObj?.elasticEmailDkimSelector);
    setSpfProvider(hasMailgun ? 'mailgun' : (hasMailchannels ? 'mailchannels' : (hasSendgrid ? 'sendgrid' : (hasElasticEmail ? 'elastic_email' : 'smtp_com'))));
    setCustomSpfValue('');
    setSmtpComDkimTxtValue('');
    setSkipDkim(false);
    setMailchannelsAuthId(String(domainObj?.mailchannelsAuthId || '').trim());
    setMailchannelsDkimSelector(domainObj?.mailchannelsDkimSelector ? normalizeMailgunSelectorInput(String(domainObj.mailchannelsDkimSelector), domain) : '');
    setMailchannelsDkimTxtValue(String(domainObj?.mailchannelsDkimTxtValue || '').trim());
    setMailgunDkimSelector(domainObj?.mailgunDkimSelector ? normalizeMailgunSelectorInput(String(domainObj.mailgunDkimSelector), domain) : '');
    setMailgunDkimTxtValue(''); // TXT value not stored in domain; user pastes from Mailgun to update
    setElasticEmailDkimSelector(domainObj?.elasticEmailDkimSelector ? normalizeMailgunSelectorInput(String(domainObj.elasticEmailDkimSelector), domain) : 'api');
    setElasticEmailDkimTxtValue('');
  }, [show, domain, isStaging, domainObj?.accountId, domainObj?.mailchannelsAuthId, domainObj?.mailchannelsDkimSelector, domainObj?.mailchannelsDkimTxtValue, domainObj?.mailgunDkimSelector, domainObj?.elasticEmailDkimSelector, domainObj?.authProvider, domainObj?.spfProvider]);

  useEffect(() => {
    if (spfProvider !== 'mailchannels') {
      setMailchannelsDkimInlineStatus(null);
      setShowMailchannelsApiKeyConnect(false);
      setMailchannelsApiKeyDraft('');
    }
  }, [spfProvider]);

  const getAuthHeader = async () => {
    await waitForAuthFunctions();
    const tg = getTokenGetter();
    if (!tg) return {};
    try {
      const token = await tg();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  };

  const bindAwsAccount = async () => {
    if (!domain || !boundAccountId) return;
    setError(null);
    setInfo(null);
    try {
      const authHeader = await getAuthHeader();
      const resp = await fetch(`${getApiUrl()}/domains/${encodeURIComponent(domain)}/bind-aws-account`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ accountId: boundAccountId })
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.message || json?.error || 'Failed to bind AWS account');
      setInfo('AWS account bound to this domain. You can now apply DNS automatically.');
    } catch (e) {
      setError(e?.message || 'Failed to bind AWS account');
    }
  };

  const consolidatedRecords = useMemo(() => {
    const dnsRecords = dnsSetupResp?.data?.dnsRecords;
    const records = flattenDnsSetupRecords(dnsRecords);

    // SES is soft-retired: when a non-SES provider is selected, hide SES-only suggestions.
    if (spfProvider !== 'ses') {
      const filtered = records.filter(r => {
        const name = String(r?.name || '').toLowerCase();
        const value = String(r?.value || '').toLowerCase();
        if (name.startsWith('_amazonses.')) return false;
        if (value.includes('amazonses.com')) return false;
        if (value.includes('feedback-smtp.')) return false;
        if (value.includes('.dkim.amazonses.com')) return false;
        return true;
      });
      records.length = 0;
      records.push(...filtered);
    }

    // Override DMARC record to reflect the wizard-selected policy/email (and env defaults)
    const effectivePolicy = isStaging ? 'none' : dmarcPolicy;
    const effectiveEmail = dmarcEmail || (domain ? `dmarc@${domain}` : '');
    const dmarcIdx = records.findIndex(r => String(r.name || '').toLowerCase().startsWith('_dmarc.'));
    const dmarcValue = buildDmarcValue({ domain, policy: effectivePolicy, ruaEmail: effectiveEmail });

    if (dmarcIdx >= 0) {
      records[dmarcIdx] = { ...records[dmarcIdx], value: dmarcValue };
    } else if (domain) {
      records.push({ type: 'TXT', name: `_dmarc.${domain}`, value: dmarcValue, purpose: 'DMARC - Email authentication policy' });
    }

    // Override SPF record based on selected provider (unless keep).
    const spfIdx = records.findIndex(
      r => String(r.type || '').toUpperCase() === 'TXT' &&
        String(r.name || '').toLowerCase() === domain.toLowerCase() &&
        String(r.value || '').toLowerCase().includes('v=spf1')
    );
    const existingSpfValue = spfIdx >= 0 ? records[spfIdx].value : '';
    const spfValue = buildSpfValue({ providerValue: spfProvider, existingValue: existingSpfValue, customValue: customSpfValue });
    if (spfValue) {
      if (spfIdx >= 0) {
        records[spfIdx] = { ...records[spfIdx], value: spfValue };
      } else if (domain) {
        records.push({ type: 'TXT', name: domain, value: spfValue, purpose: 'SPF - Authorized senders' });
      }
    }

    // SMTP.com DKIM TXT record (provider-specific)
    if (spfProvider === 'smtp_com' && domain) {
      const dkimName = `smtpcomcustomers._domainkey.${domain}`;
      const dkimValue = normalizeDkimTxtValue(smtpComDkimTxtValue);
      if (dkimValue) {
        // remove any legacy SES DKIM suggestions (this flow is DNS-only, no SES)
        const withoutSesDkim = records.filter(r => !(String(r.type || '').toUpperCase() === 'CNAME' && String(r.value || '').includes('.dkim.amazonses.com')));
        records.length = 0;
        records.push(...withoutSesDkim);
        records.push({ type: 'TXT', name: dkimName, value: dkimValue, purpose: 'DKIM - SMTP.com signing key' });
      }
    }

    // MailChannels _mailchannels auth TXT (required for relay)
    if (spfProvider === 'mailchannels' && domain) {
      const authValue = (mailchannelsAuthId || '').trim() || 'YOUR_MAILCHANNELS_ACCOUNT_ID';
      records.push({ type: 'TXT', name: `_mailchannels.${domain}`, value: `v=mc1 auth=${authValue}`, purpose: 'MailChannels relay auth (required)' });
      const normalizedMailchannelsSelector = normalizeMailgunSelectorInput(mailchannelsDkimSelector, domain);
      const normalizedMailchannelsDkim = String(mailchannelsDkimTxtValue || '').trim() || 'v=DKIM1; p=...';
      if (normalizedMailchannelsSelector) {
        records.push({
          type: 'TXT',
          name: `${normalizedMailchannelsSelector}._domainkey.${domain}`,
          value: normalizedMailchannelsDkim,
          purpose: 'MailChannels DKIM (recommended)'
        });
      }
    }

    // Mailgun DKIM (selector._domainkey.<domain>; value from Mailgun dashboard)
    const normalizedMailgunSelector = normalizeMailgunSelectorInput(mailgunDkimSelector, domain);
    if (spfProvider === 'mailgun' && domain && normalizedMailgunSelector) {
      const v = (mailgunDkimTxtValue || '').trim() || 'v=DKIM1; k=rsa; p=...';
      records.push({ type: 'TXT', name: `${normalizedMailgunSelector}._domainkey.${domain}`, value: v, purpose: 'Mailgun DKIM (required)' });
    }

    // Elastic Email DKIM (usually api._domainkey.<domain>; TXT from Elastic Email dashboard)
    const normalizedElasticSelector = normalizeMailgunSelectorInput(elasticEmailDkimSelector || 'api', domain) || 'api';
    if (spfProvider === 'elastic_email' && domain && normalizedElasticSelector) {
      const v = (elasticEmailDkimTxtValue || '').trim() || 'v=DKIM1; p=...';
      records.push({ type: 'TXT', name: `${normalizedElasticSelector}._domainkey.${domain}`, value: v, purpose: 'Elastic Email DKIM (required)' });
    }

    return records;
  }, [dnsSetupResp, domain, dmarcPolicy, dmarcEmail, isStaging, spfProvider, customSpfValue, smtpComDkimTxtValue, mailchannelsAuthId, mailchannelsDkimSelector, mailchannelsDkimTxtValue, mailgunDkimSelector, mailgunDkimTxtValue, elasticEmailDkimSelector, elasticEmailDkimTxtValue]);

  const spfPreviewLine = useMemo(() => {
    if (!domain) return '';
    const dnsRecords = dnsSetupResp?.data?.dnsRecords;
    const records = flattenDnsSetupRecords(dnsRecords);
    const spfIdx = records.findIndex(
      r => String(r.type || '').toUpperCase() === 'TXT' &&
        String(r.name || '').toLowerCase() === domain.toLowerCase() &&
        String(r.value || '').toLowerCase().includes('v=spf1')
    );
    const existingSpfValue = spfIdx >= 0 ? records[spfIdx].value : '';
    const spfValue = buildSpfValue({ providerValue: spfProvider, existingValue: existingSpfValue, customValue: customSpfValue });
    if (!spfValue) return '';
    return `${domain}  TXT  ${spfValue}`;
  }, [dnsSetupResp, domain, spfProvider, customSpfValue]);

  const copyAll = async () => {
    const text = consolidatedRecords.map(formatRecordLine).join('\n');
    await navigator.clipboard.writeText(text);
    setInfo('Copied DNS records to clipboard.');
  };

  const connectMailchannelsApiKeyAndRetry = async () => {
    if (!domain || spfProvider !== 'mailchannels') return;
    const apiKey = String(mailchannelsApiKeyDraft || '').trim();
    if (!apiKey) {
      setMailchannelsDkimInlineStatus({ variant: 'danger', message: 'Please paste your MailChannels API key.' });
      return;
    }
    if (isLikelyEmailAddress(apiKey)) {
      setMailchannelsDkimInlineStatus({
        variant: 'danger',
        message: 'MailChannels API key is invalid. Do not paste an email address.'
      });
      return;
    }
    setError(null);
    setInfo(null);
    setIsSavingMailchannelsApiKey(true);
    setMailchannelsDkimInlineStatus({ variant: 'secondary', message: 'Saving MailChannels API key...' });
    try {
      const authHeader = await getAuthHeader();
      const resp = await fetch(`${getApiUrl()}/domains/${encodeURIComponent(domain)}/mailchannels-api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          apiKey,
          poolId: MAILCHANNELS_DEFAULT_POOL_ID,
          scope: 'pool'
        })
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = getFriendlyMailchannelsConnectError(
          json?.code,
          json?.messageKey,
          json?.message || json?.error || 'Could not save MailChannels API key.'
        );
        setMailchannelsDkimInlineStatus({ variant: 'danger', message: msg });
        return;
      }

      setShowMailchannelsApiKeyConnect(false);
      setMailchannelsApiKeyDraft('');
      setMailchannelsDkimInlineStatus({ variant: 'secondary', message: 'MailChannels connected. Generating DKIM...' });
      await generateMailchannelsDkim();
    } catch (e) {
      const msg = getFriendlyMailchannelsConnectError(
        '',
        '',
        e?.message || 'Could not save MailChannels API key right now.'
      );
      setMailchannelsDkimInlineStatus({ variant: 'danger', message: msg });
    } finally {
      setIsSavingMailchannelsApiKey(false);
    }
  };

  const generateMailchannelsDkim = async () => {
    if (!domain || spfProvider !== 'mailchannels') return;
    setError(null);
    setInfo(null);
    setIsGeneratingMailchannelsDkim(true);
    setMailchannelsDkimInlineStatus({
      variant: 'secondary',
      message: 'Requesting DKIM key from MailChannels...'
    });
    try {
      const authHeader = await getAuthHeader();
      // Keep existing selector idempotent when one is already known for this domain.
      // If empty, backend will auto-select (mc1, mc2, ...).
      const existingSelectorHint = normalizeMailgunSelectorInput(mailchannelsDkimSelector, domain);
      const resp = await fetch(`${getApiUrl()}/domains/${encodeURIComponent(domain)}/mailchannels-dkim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          selector: existingSelectorHint || undefined
        })
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const rawCode = json?.code || '';
        const rawMessageKey = json?.messageKey || '';
        const rawMsg = json?.message || json?.error || 'Failed to generate MailChannels DKIM';
        if (isMailchannelsApiKeyMissingSignal(rawCode, rawMessageKey, rawMsg)) {
          setShowMailchannelsApiKeyConnect(true);
          setMailchannelsDkimInlineStatus({
            variant: 'warning',
            message: 'Connect MailChannels to enable automatic DKIM generation.'
          });
          setError(null);
          return;
        }
        if (isMailchannelsApiKeyInvalidSignal(rawCode, rawMessageKey, rawMsg)) {
          setShowMailchannelsApiKeyConnect(true);
          setMailchannelsDkimInlineStatus({
            variant: 'danger',
            message: 'MailChannels API key is invalid. Paste a valid API key and try again.'
          });
          setError(null);
          return;
        }
        const msg = getFriendlyMailchannelsDkimGenerateError(rawMsg);
        setMailchannelsDkimInlineStatus({ variant: 'danger', message: msg });
        setError(null);
        return;
      }
      const selector = normalizeMailgunSelectorInput(json?.data?.selector || existingSelectorHint, domain);
      const txtValue = String(json?.data?.dkimTxtValue || '').trim();
      if (!selector || !txtValue) {
        const msg = 'MailChannels returned an incomplete DKIM response. Click Generate again.';
        setMailchannelsDkimInlineStatus({ variant: 'danger', message: msg });
        setError(null);
        return;
      }
      setMailchannelsDkimSelector(selector);
      setMailchannelsDkimTxtValue(txtValue);
      setShowMailchannelsApiKeyConnect(false);
      setMailchannelsApiKeyDraft('');
      setMailchannelsDkimInlineStatus({
        variant: 'success',
        message: `DKIM ${json?.data?.source === 'created' ? 'generated' : 'loaded'} and fields auto-filled (${selector}._domainkey.${domain}).`
      });
      setInfo(`MailChannels DKIM ${json?.data?.source === 'created' ? 'generated' : 'loaded'} (${selector}._domainkey.${domain}). Click Apply automatically to write Route53.`);
    } catch (e) {
      const msg = getFriendlyMailchannelsDkimGenerateError(e?.message || 'Failed to generate MailChannels DKIM');
      setMailchannelsDkimInlineStatus({
        variant: 'danger',
        message: msg
      });
      // Keep generate-specific failures local to this section so users don't miss context.
      setError(null);
    } finally {
      setIsGeneratingMailchannelsDkim(false);
    }
  };

  const applyAutomatically = async () => {
    if (!domain) return;
    if (!effectiveAccountId) {
      setError('This domain is not yet linked to an AWS account in Kross Global, so we cannot safely write Route53. Bind the owning AWS account below, or use manual DNS.');
      return;
    }
    if (spfProvider === 'smtp_com' && !skipDkim && !normalizeDkimTxtValue(smtpComDkimTxtValue)) {
      setError('SMTP.com DKIM is required for an atomic Apply. Paste the DKIM TXT value (k=rsa; p=...) or check “Skip DKIM”.');
      return;
    }
    if (spfProvider === 'mailchannels' && !String(mailchannelsAuthId || '').trim()) {
      setError('MailChannels Account ID is required for Apply. Enter your MailChannels account ID (for _mailchannels auth=).');
      return;
    }
    if (spfProvider === 'mailchannels' && (!String(mailchannelsDkimSelector || '').trim() || !String(mailchannelsDkimTxtValue || '').trim())) {
      setError('MailChannels DKIM is required for Apply. Click "Generate DKIM in MailChannels" to auto-generate selector + TXT, then Apply.');
      return;
    }
    if (spfProvider === 'mailgun' && (!String(mailgunDkimSelector || '').trim() || !String(mailgunDkimTxtValue || '').trim())) {
      setError('Mailgun DKIM is required for Apply. Enter the DKIM selector and TXT value from the Mailgun dashboard (Sending → Domains).');
      return;
    }
    if (spfProvider === 'elastic_email' && (!String(elasticEmailDkimSelector || '').trim() || !String(elasticEmailDkimTxtValue || '').trim())) {
      setError('Elastic Email DKIM is required for Apply. Enter the DKIM selector and TXT value from the Elastic Email dashboard (typically selector "api").');
      return;
    }

    setError(null);
    setInfo(null);
    setIsApplying(true);
    try {
      const authHeader = await getAuthHeader();
      // Step A: ask backend to detect conflicts (no changes applied when handleExistingRecords=ask and conflicts exist)
      const resp = await fetch(`${getApiUrl()}/domains/${encodeURIComponent(domain)}/configure-dns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          accountId: effectiveAccountId,
          handleExistingRecords: 'ask',
          spfProvider,
          spfCustomValue: spfProvider === 'custom' ? customSpfValue : undefined,
          // If the current SPF is SES-only (legacy), we can safely replace it.
          // Otherwise backend will merge conservatively.
          spfStrategy: 'replace_ses_only',
          smtpComDkimTxtValue: (spfProvider === 'smtp_com' && !skipDkim) ? normalizeDkimTxtValue(smtpComDkimTxtValue) : undefined,
          mailchannelsAuthId: spfProvider === 'mailchannels' ? String(mailchannelsAuthId || '').trim() : undefined,
          mailchannelsDkimSelector: spfProvider === 'mailchannels' ? normalizeMailgunSelectorInput(mailchannelsDkimSelector, domain) : undefined,
          mailchannelsDkimTxtValue: spfProvider === 'mailchannels' ? String(mailchannelsDkimTxtValue || '').trim() : undefined,
          mailgunDkimSelector: spfProvider === 'mailgun' ? normalizeMailgunSelectorInput(mailgunDkimSelector, domain) : undefined,
          mailgunDkimTxtValue: spfProvider === 'mailgun' ? String(mailgunDkimTxtValue || '').trim() : undefined,
          elasticEmailDkimSelector: spfProvider === 'elastic_email' ? normalizeMailgunSelectorInput(elasticEmailDkimSelector || 'api', domain) : undefined,
          elasticEmailDkimTxtValue: spfProvider === 'elastic_email' ? String(elasticEmailDkimTxtValue || '').trim() : undefined
        })
      });
      const json = await resp.json();
      if (!resp.ok) {
        if (json?.multipleSpfNeedsAttention) {
          setError(json?.message || 'Multiple SPF TXT records at apex. Consolidate to a single SPF record in Route53 before using Apply.');
          return;
        }
        throw new Error(json?.message || json?.error || 'Failed to apply DNS automatically');
      }

      if (json?.nameserversNeedUpdate) {
        setInfo(json?.data?.message || 'Nameservers do not match the Route53 hosted zone yet. Please update nameservers, then re-try Apply.');
        setStep('plan');
        return;
      }

      if (json?.manualSetupRequired) {
        setInfo(json?.data?.message || 'This domain is not managed by Route53 in the selected AWS account. Please add the records manually, then verify.');
        setStep('verify');
        return;
      }

      if (json?.existingRecordsFound) {
        setExistingRecords(json?.data?.existingRecords || []);
        setStep('apply');
        setInfo('Existing DNS records found. Choose how to proceed, then apply.');
        return;
      }

      // Step B: apply DMARC policy (Route53 UPSERT) to match wizard selection
      const effectivePolicy = isStaging ? 'none' : dmarcPolicy;
      await updateDMARC({
        domain,
        dmarcEmail,
        dmarcPolicy: effectivePolicy,
        accountId: boundAccountId
      }).unwrap();

      const applied = json?.data?.recordsApplied;
      if (applied) {
        const parts = [
          applied.spf ? 'SPF ✅' : 'SPF ⚠️',
          applied.dmarc ? 'DMARC ✅' : 'DMARC ⚠️',
          applied.dkim ? 'DKIM ✅' : `DKIM ⚠️ (${applied.dkimReason || 'skipped'})`
        ];
        setInfo(`Applied: ${parts.join(' · ')}. Next: verify.`);
      } else {
        setInfo('DNS records applied. Next: verify.');
      }
      setStep('verify');
      onAuthSuccess?.();
    } catch (e) {
      setError(e?.message || 'Failed to apply DNS automatically');
    } finally {
      setIsApplying(false);
    }
  };

  const applyWithChoice = async () => {
    if (!domain || !effectiveAccountId) return;
    if (spfProvider === 'smtp_com' && !skipDkim && !normalizeDkimTxtValue(smtpComDkimTxtValue)) {
      setError('SMTP.com DKIM is required for an atomic Apply. Paste the DKIM TXT value (k=rsa; p=...) or check “Skip DKIM”.');
      return;
    }
    if (spfProvider === 'mailchannels' && !String(mailchannelsAuthId || '').trim()) {
      setError('MailChannels Account ID is required for Apply. Enter your MailChannels account ID (for _mailchannels auth=).');
      return;
    }
    if (spfProvider === 'mailchannels' && (!String(mailchannelsDkimSelector || '').trim() || !String(mailchannelsDkimTxtValue || '').trim())) {
      setError('MailChannels DKIM is required for Apply. Click "Generate DKIM in MailChannels" to auto-generate selector + TXT, then Apply.');
      return;
    }
    if (spfProvider === 'mailgun' && (!String(mailgunDkimSelector || '').trim() || !String(mailgunDkimTxtValue || '').trim())) {
      setError('Mailgun DKIM is required for Apply. Enter the DKIM selector and TXT value from the Mailgun dashboard (Sending → Domains).');
      return;
    }
    if (spfProvider === 'elastic_email' && (!String(elasticEmailDkimSelector || '').trim() || !String(elasticEmailDkimTxtValue || '').trim())) {
      setError('Elastic Email DKIM is required for Apply. Enter the DKIM selector and TXT value from the Elastic Email dashboard (typically selector "api").');
      return;
    }
    setError(null);
    setInfo(null);
    setIsApplying(true);
    try {
      const authHeader = await getAuthHeader();
      const resp = await fetch(`${getApiUrl()}/domains/${encodeURIComponent(domain)}/configure-dns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          accountId: boundAccountId,
          handleExistingRecords: existingRecordsChoice,
          spfProvider,
          spfCustomValue: spfProvider === 'custom' ? customSpfValue : undefined,
          spfStrategy: 'replace_ses_only',
          smtpComDkimTxtValue: (spfProvider === 'smtp_com' && !skipDkim) ? normalizeDkimTxtValue(smtpComDkimTxtValue) : undefined,
          mailchannelsAuthId: spfProvider === 'mailchannels' ? String(mailchannelsAuthId || '').trim() : undefined,
          mailchannelsDkimSelector: spfProvider === 'mailchannels' ? normalizeMailgunSelectorInput(mailchannelsDkimSelector, domain) : undefined,
          mailchannelsDkimTxtValue: spfProvider === 'mailchannels' ? String(mailchannelsDkimTxtValue || '').trim() : undefined,
          mailgunDkimSelector: spfProvider === 'mailgun' ? normalizeMailgunSelectorInput(mailgunDkimSelector, domain) : undefined,
          mailgunDkimTxtValue: spfProvider === 'mailgun' ? String(mailgunDkimTxtValue || '').trim() : undefined,
          elasticEmailDkimSelector: spfProvider === 'elastic_email' ? normalizeMailgunSelectorInput(elasticEmailDkimSelector || 'api', domain) : undefined,
          elasticEmailDkimTxtValue: spfProvider === 'elastic_email' ? String(elasticEmailDkimTxtValue || '').trim() : undefined
        })
      });
      const json = await resp.json();
      if (!resp.ok) {
        if (json?.multipleSpfNeedsAttention) {
          setError(json?.message || 'Multiple SPF TXT records at apex. Consolidate to a single SPF record in Route53 before using Apply.');
          return;
        }
        throw new Error(json?.message || json?.error || 'Failed to apply DNS automatically');
      }

      if (json?.nameserversNeedUpdate) {
        setInfo(json?.data?.message || 'Nameservers do not match the Route53 hosted zone yet. Please update nameservers, then re-try Apply.');
        setStep('plan');
        return;
      }

      if (json?.manualSetupRequired) {
        setExistingRecords(null);
        setInfo(json?.data?.message || 'This domain is not managed by Route53 in the selected AWS account. Please add the records manually, then verify.');
        setStep('verify');
        return;
      }

      const effectivePolicy = isStaging ? 'none' : dmarcPolicy;
      await updateDMARC({
        domain,
        dmarcEmail,
        dmarcPolicy: effectivePolicy,
        accountId: effectiveAccountId
      }).unwrap();

      setExistingRecords(null);
      const applied = json?.data?.recordsApplied;
      if (applied) {
        const parts = [
          applied.spf ? 'SPF ✅' : 'SPF ⚠️',
          applied.dmarc ? 'DMARC ✅' : 'DMARC ⚠️',
          applied.dkim ? 'DKIM ✅' : `DKIM ⚠️ (${applied.dkimReason || 'skipped'})`
        ];
        setInfo(`Applied: ${parts.join(' · ')}. Next: verify.`);
      } else {
        setInfo('DNS records applied. Next: verify.');
      }
      setStep('verify');
      onAuthSuccess?.();
    } catch (e) {
      setError(e?.message || 'Failed to apply DNS automatically');
    } finally {
      setIsApplying(false);
    }
  };

  const fetchExistingRoute53Records = async () => {
    if (!domain || !boundAccountId) return null;
    const authHeader = await getAuthHeader();
    const resp = await fetch(`${getApiUrl()}/domains/${encodeURIComponent(domain)}/configure-dns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body: JSON.stringify({
        accountId: boundAccountId,
        listExistingOnly: true,
        spfProvider,
        spfCustomValue: spfProvider === 'custom' ? customSpfValue : undefined,
        spfStrategy: 'replace_ses_only',
        smtpComDkimTxtValue: spfProvider === 'smtp_com' ? smtpComDkimTxtValue : undefined,
        mailchannelsDkimSelector: spfProvider === 'mailchannels' ? normalizeMailgunSelectorInput(mailchannelsDkimSelector, domain) : undefined,
        mailgunDkimSelector: spfProvider === 'mailgun' ? normalizeMailgunSelectorInput(mailgunDkimSelector, domain) : undefined,
        elasticEmailDkimSelector: spfProvider === 'elastic_email' ? normalizeMailgunSelectorInput(elasticEmailDkimSelector || 'api', domain) : undefined
      })
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(json?.message || json?.error || 'Failed to fetch Route53 DNS records');
    return json;
  };

  const verifyNow = async () => {
    if (!domain) return;
    setError(null);
    setInfo(null);
    setVerifyDetails(null);
    try {
      // If we can, show the same "Existing DNS records" panel during Verify so users can see the live Route53 state.
      if (effectiveAccountId) {
        try {
          const existing = await fetchExistingRoute53Records();
          if (existing?.existingRecordsFound) {
            setExistingRecords(existing?.data?.existingRecords || []);
          }
        } catch (e) {
          // Non-blocking: still run DNS validation below.
          console.warn('Failed to fetch existing Route53 records:', e);
        }
      }

      const result = await validateDNSRecords({ domain, provider: spfProvider }).unwrap();
      const emailAuthOverall = result?.data?.emailAuth?.overallStatus;
      setVerifyDetails(result?.data?.emailAuth || null);
      setInfo(
        emailAuthOverall === 'Complete'
          ? 'Verified ✅ SPF + DMARC detected and DKIM checked. Domain list will refresh; if the row still shows Pending for DKIM/SPF, wait 1–2 minutes for DNS propagation and refresh the page.'
          : 'Partial ⚠️ Some records are still missing/propagating.'
      );
      onAuthSuccess?.();
    } catch (e) {
      setError(e?.data?.message || e?.message || 'Verify failed');
    }
  };

  // "Auto apply" should be available whenever the domain is Route53-managed (even if hostProvider metadata is missing).
  const isAwsHosted = String(domainObj?.hostProvider || '').toLowerCase() === 'aws';
  const canAutoApplyAws = Boolean(isAwsHosted || domainObj?.isInRoute53 || domainObj?.hostedZoneId);
  const effectivePolicy = isStaging ? 'none' : dmarcPolicy;
  const dmarcValuePreview = domain ? buildDmarcValue({ domain, policy: effectivePolicy, ruaEmail: dmarcEmail || `dmarc@${domain}` }) : '';

  // On Verify step: consider "all verified" when we have results and all three checks pass.
  const allVerified =
    step === 'verify' &&
    verifyDetails &&
    verifyDetails.spf?.ok &&
    verifyDetails.dkim?.ok &&
    verifyDetails.dmarc?.isConfigured;

  // Don't gate Apply on account selection when hosted === aws AND accountId empty: use main account so UI never blocks "unlinked" main-account domains.
  const effectiveAccountId = boundAccountId || (canAutoApplyAws ? MAIN_AWS_ACCOUNT_ID : '');

  const footer = (
    <>
      <Button variant="secondary" onClick={onHide} disabled={isApplying || isUpdatingDmarc || isValidating}>
        Close
      </Button>
      {step === 'plan' && (
        <>
          <Button variant="outline-primary" onClick={copyAll} disabled={!consolidatedRecords.length}>
            Copy all DNS
          </Button>
          {canAutoApplyAws ? (
            <Button variant="primary" onClick={applyAutomatically} disabled={isApplying || isUpdatingDmarc || !domain}>
              {isApplying || isUpdatingDmarc ? <><Spinner size="sm" className="me-2" animation="border" />Applying…</> : 'Apply automatically (AWS)'}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => setStep('verify')} disabled={!domain}>
              I added these records → Verify
            </Button>
          )}
        </>
      )}
      {step === 'apply' && (
        <Button variant="primary" onClick={applyWithChoice} disabled={isApplying || isUpdatingDmarc}>
          {isApplying || isUpdatingDmarc ? <><Spinner size="sm" className="me-2" animation="border" />Applying…</> : 'Apply now'}
        </Button>
      )}
      {step === 'verify' && (
        allVerified ? (
          <Button variant="success" onClick={() => { onAuthSuccess?.(); onHide(); }}>
            Done — all verified ✓
          </Button>
        ) : (
          <Button variant="primary" onClick={verifyNow} disabled={isValidating}>
            {isValidating ? <><Spinner size="sm" className="me-2" animation="border" />Verifying…</> : 'Verify now'}
          </Button>
        )
      )}
    </>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Email Authentication (SPF / DKIM / DMARC){' '}
          {domain ? <Badge bg="secondary" className="ms-2">{domain}</Badge> : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {info ? <Alert variant="info">{info}</Alert> : null}

        {canAutoApplyAws && !boundAccountId ? (
          <Alert variant="info">
            <div className="mb-2">
              <strong>Using main AWS account (982423662433)</strong> — this domain is in the current account, so we’ll write Route53 there. No need to select an account unless this domain is in a different AWS account.
            </div>
            <Form.Group className="mb-2">
              <Form.Label>Override: AWS account that owns the Route53 hosted zone (optional)</Form.Label>
              <Form.Select value={boundAccountId} onChange={(e) => setBoundAccountId(e.target.value)}>
                <option value="">Use main account (982423662433)</option>
                {awsAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.alias || a.name || a.id} ({a.id})
                  </option>
                ))}
              </Form.Select>
              <div className="text-muted small mt-1">
                Only change this if the domain’s hosted zone is in another AWS account.
              </div>
            </Form.Group>
          </Alert>
        ) : null}

        {dnsSetupError ? (
          <Alert variant="warning">
            Could not load DNS setup from API. You can still proceed, but records may be incomplete.
            <div className="mt-2">
              <Button size="sm" variant="outline-secondary" onClick={() => refetchDnsSetup()}>
                Retry
              </Button>
            </div>
          </Alert>
        ) : null}

        <div className="d-flex align-items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
          <Badge bg={step === 'plan' ? 'primary' : 'secondary'}>Plan</Badge>
          <Badge bg={step === 'apply' ? 'primary' : 'secondary'}>Apply</Badge>
          <Badge bg={step === 'verify' ? 'primary' : 'secondary'}>Verify</Badge>
        </div>

        <Form>
          <Form.Group className="mb-2">
            <Form.Label>DMARC policy (recommended)</Form.Label>
            <Form.Select
              value={effectivePolicy}
              onChange={(e) => setDmarcPolicy(e.target.value)}
              disabled={isStaging}
            >
              {DMARC_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Form.Select>
            <Form.Text muted>
              {isStaging
                ? 'Staging is locked to monitor-only (p=none).'
                : 'Production defaults to monitor-only (p=none). You can enforce later once Verified.'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>DMARC report email (rua)</Form.Label>
            <Form.Control
              value={dmarcEmail}
              onChange={(e) => setDmarcEmail(e.target.value)}
              placeholder={domain ? `dmarc@${domain}` : 'dmarc@yourdomain.com'}
            />
            <Form.Text muted>
              DMARC is considered configured if `_dmarc.{domain}` exists and parses. rua is recommended, but we won’t block verification on it.
            </Form.Text>
            {String(tenantDmarcEmail || '').trim() && dmarcEmail === String(tenantDmarcEmail || '').trim() ? (
              <Alert variant="info" className="mt-2 py-1 small mb-0">
                Using tenant default (<strong>{tenantDmarcEmail}</strong>). You can override per domain.
              </Alert>
            ) : !String(tenantDmarcEmail || '').trim() ? (
              <Alert variant="warning" className="mt-2 py-1 small mb-0">
                No tenant DMARC email configured. Set a default in Company Profile so all domains use one central inbox.
              </Alert>
            ) : null}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>SPF source (outbound provider)</Form.Label>
            <Form.Select
              value={spfProvider}
              onChange={(e) => setSpfProvider(e.target.value)}
            >
              {SPF_PROVIDERS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Form.Select>
            <Form.Text muted>
              Choose the outbound provider your SPF should authorize. “Keep existing SPF” leaves your current record unchanged.
            </Form.Text>
          </Form.Group>

          {spfProvider === 'custom' ? (
            <Form.Group className="mb-3">
              <Form.Label>Custom SPF (advanced)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={customSpfValue}
                onChange={(e) => setCustomSpfValue(e.target.value)}
                placeholder="Example: include:_spf.smtp.com include:_spf.google.com ~all"
              />
              <Form.Text muted>
                Paste a full SPF record (starting with <code>v=spf1</code>) or just the mechanisms (we’ll prefix with <code>v=spf1</code>).
              </Form.Text>
            </Form.Group>
          ) : null}

          {spfProvider === 'sendgrid' ? (
            <Alert variant="info" className="mb-3 py-2 small">
              <strong>Twilio SendGrid note:</strong> this Authentication modal updates SPF/DMARC only for SendGrid.
              SendGrid DKIM/domain-auth records are managed in <strong>Outbound set</strong>, not here.
              On the Domains page click <strong>Outbound set</strong> for this domain, select <strong>Twilio SendGrid</strong>,
              then use the &quot;SendGrid DNS records&quot; block and click <strong>Update Route 53</strong> to write
              the <code>s1._domainkey</code>/<code>s2._domainkey</code> CNAME records from SendGrid.
            </Alert>
          ) : null}

          {spfProvider === 'smtp_com' ? (
            <Form.Group className="mb-3">
              <Form.Label>DKIM (SMTP.com)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={smtpComDkimTxtValue}
                onChange={(e) => setSmtpComDkimTxtValue(e.target.value)}
                placeholder="Paste SMTP.com DKIM TXT value (k=rsa; p=...)"
              />
              <Form.Text muted>
                This will create/update a TXT record at <code>smtpcomcustomers._domainkey.{domain}</code> in Route53.
              </Form.Text>
              <Form.Check
                className="mt-2"
                type="checkbox"
                checked={skipDkim}
                onChange={(e) => setSkipDkim(e.target.checked)}
                label="Skip DKIM (apply SPF/DMARC only)"
              />
            </Form.Group>
          ) : null}

          {spfProvider === 'mailchannels' ? (
            <Form.Group className="mb-3">
              <Form.Label>MailChannels Account ID (for <code>_mailchannels</code> auth=)</Form.Label>
              <Form.Control
                type="text"
                value={mailchannelsAuthId}
                onChange={(e) => setMailchannelsAuthId(e.target.value)}
                placeholder="Your MailChannels account ID from dashboard"
              />
              <Form.Text muted>
                Required for relay. Apply writes the <code>_mailchannels.{domain}</code> TXT record (<code>v=mc1 auth=YOUR_ID</code>) to Route53 when you Apply.
              </Form.Text>
              <Form.Text muted className="d-block mt-1">
                If <code>_mailchannels</code> is missing in DNS (e.g. domain was set up before this), re-run Apply with MailChannels and this Account ID, or add the TXT manually in Route 53.
              </Form.Text>
              <Form.Text muted className="d-block mt-1">
                Confirm in the MailChannels dashboard that this domain is an authorized sending domain and SMTP relay is enabled.
              </Form.Text>
              <hr />
              <Form.Label>MailChannels DKIM selector</Form.Label>
              <div className="small text-muted mb-2">
                We auto-generate this when you click <strong>Generate DKIM in MailChannels</strong>.
                {mailchannelsDkimSelector ? (
                  <> Current selector: <code>{mailchannelsDkimSelector}</code>.</>
                ) : null}
              </div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={generateMailchannelsDkim}
                  disabled={isGeneratingMailchannelsDkim || isSavingMailchannelsApiKey || !domain}
                >
                  {isGeneratingMailchannelsDkim ? (
                    <><Spinner size="sm" className="me-2" animation="border" />Generating…</>
                  ) : (
                    'Generate DKIM in MailChannels'
                  )}
                </Button>
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Creates/retrieves a DKIM key pair and auto-fills selector + TXT.
                </span>
              </div>
              {showMailchannelsApiKeyConnect ? (
                <Alert variant="light" className="border py-2 mb-2">
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }} className="mb-2">
                    Connect your MailChannels account
                  </div>
                  <Form.Label className="small mb-1">MailChannels API Key</Form.Label>
                  <Form.Control
                    type="password"
                    value={mailchannelsApiKeyDraft}
                    onChange={(e) => setMailchannelsApiKeyDraft(e.target.value)}
                    placeholder="Paste your MailChannels API key"
                    autoComplete="off"
                  />
                  <Form.Text muted className="d-block mt-1">
                    We store this securely and retry DKIM generation automatically.
                  </Form.Text>
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={connectMailchannelsApiKeyAndRetry}
                      disabled={isSavingMailchannelsApiKey || isGeneratingMailchannelsDkim || !String(mailchannelsApiKeyDraft || '').trim()}
                    >
                      {isSavingMailchannelsApiKey ? (
                        <><Spinner size="sm" className="me-2" animation="border" />Saving…</>
                      ) : (
                        'Save & Continue'
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setShowMailchannelsApiKeyConnect(false)}
                      disabled={isSavingMailchannelsApiKey}
                    >
                      Cancel
                    </Button>
                  </div>
                </Alert>
              ) : null}
              {mailchannelsDkimInlineStatus ? (
                <Alert variant={mailchannelsDkimInlineStatus.variant} className="py-2 mb-2">
                  <span style={{ fontSize: '0.85rem' }}>{mailchannelsDkimInlineStatus.message}</span>
                </Alert>
              ) : null}
              <Form.Label>MailChannels DKIM TXT value</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={mailchannelsDkimTxtValue}
                onChange={(e) => setMailchannelsDkimTxtValue(e.target.value)}
                placeholder="e.g. v=DKIM1; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8A..."
              />
              <Form.Text muted className="d-block mt-1">
                Use the button above to generate selector + TXT automatically, or paste TXT manually. Apply writes <code>{normalizeMailgunSelectorInput(mailchannelsDkimSelector, domain) || 'auto-selector'}._domainkey.{domain}</code> to Route53.
              </Form.Text>
              <Form.Text muted className="d-block mt-1">
                Reference: <a href="https://docs.mailchannels.net/email-api/api-reference/create-dkim-key-pair" target="_blank" rel="noreferrer">MailChannels Create DKIM Key Pair API</a>.
              </Form.Text>
            </Form.Group>
          ) : null}

          {spfProvider === 'mailgun' ? (
            <Form.Group className="mb-3">
              <Form.Label>Mailgun DKIM (from Sending → Domains)</Form.Label>
              <Form.Control
                type="text"
                className="mb-2"
                value={mailgunDkimSelector}
                onChange={(e) => setMailgunDkimSelector(e.target.value)}
                placeholder="Selector (e.g. k1 or selector1)"
              />
              <Form.Control
                as="textarea"
                rows={3}
                value={mailgunDkimTxtValue}
                onChange={(e) => setMailgunDkimTxtValue(e.target.value)}
                placeholder="TXT value (e.g. v=DKIM1; k=rsa; p=MIIBIjAN...)"
              />
              <Form.Text muted>
                Apply writes SPF (<code>include:mailgun.org</code>) and the DKIM TXT at <code>{normalizeMailgunSelectorInput(mailgunDkimSelector, domain) || 'selector'}._domainkey.{domain}</code> to Route53. Get both from Mailgun dashboard → Sending → Domains → your domain.
              </Form.Text>
              <Form.Text muted className="d-block mt-1">
                Free plan: you may need to verify your account and domain in Mailgun before sending to arbitrary recipients. Expect &quot;rejected / domain not verified&quot; until the domain is fully verified in the Mailgun dashboard.
              </Form.Text>
            </Form.Group>
          ) : null}

          {spfProvider === 'elastic_email' ? (
            <Form.Group className="mb-3">
              <Form.Label>Elastic Email DKIM (from Settings → Domains)</Form.Label>
              <Form.Control
                type="text"
                className="mb-2"
                value={elasticEmailDkimSelector}
                onChange={(e) => setElasticEmailDkimSelector(e.target.value)}
                placeholder="Selector (usually: api)"
              />
              <Form.Control
                as="textarea"
                rows={3}
                value={elasticEmailDkimTxtValue}
                onChange={(e) => setElasticEmailDkimTxtValue(e.target.value)}
                placeholder="TXT value (e.g. v=DKIM1; p=MIIBIjAN...)"
              />
              <Form.Text muted>
                Apply writes SPF (<code>include:_spf.elasticemail.com</code>) and DKIM TXT at <code>{normalizeMailgunSelectorInput(elasticEmailDkimSelector || 'api', domain) || 'api'}._domainkey.{domain}</code> to Route53.
              </Form.Text>
              <Form.Text muted className="d-block mt-1">
                Elastic Email commonly uses selector <code>api</code>. Use the exact selector/TXT shown in your Elastic Email domain authentication settings.
              </Form.Text>
            </Form.Group>
          ) : null}
        </Form>

        <div className="mb-2">
          <strong>DMARC record preview</strong>
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            {_dmarcPreviewLine(domain, dmarcValuePreview)}
          </div>
        </div>

        <div className="mb-2">
          <strong>SPF record preview</strong>
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            {spfPreviewLine || 'No SPF change will be applied.'}
          </div>
        </div>

        {(step === 'apply' || step === 'verify') && existingRecords ? (
          <Alert variant="warning">
            <div className="d-flex align-items-center justify-content-between" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div>
                <strong>{step === 'verify' ? 'Current Route53 DNS records' : 'Existing DNS records detected'}</strong>
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                  {step === 'verify'
                    ? 'This is the live Route53 view for this domain (read-only).'
                    : 'Choose how to proceed. “Keep/UPSERT” is safer than delete.'}
                </div>
              </div>
              {step === 'apply' ? (
                <Form.Select style={{ width: 260 }} value={existingRecordsChoice} onChange={(e) => setExistingRecordsChoice(e.target.value)}>
                  <option value="keep">Keep (UPSERT / update in place)</option>
                  <option value="delete">Delete conflicting records first</option>
                </Form.Select>
              ) : null}
            </div>
            <div className="mt-2">
              <Table size="sm" bordered responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Values</th>
                  </tr>
                </thead>
                <tbody>
                  {existingRecords.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.name}</td>
                      <td>{r.type}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{(r.values || []).join(' , ')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Alert>
        ) : null}

        {step === 'verify' && allVerified ? (
          <Alert variant="success">
            <strong>All checks passed.</strong> SPF, DKIM, and DMARC are configured. You can close this modal or click &quot;Done — all verified ✓&quot; below.
          </Alert>
        ) : null}
        {step === 'verify' && verifyDetails ? (
          <Alert variant="light" className="border">
            <div className="mb-2">
              <strong>Verification details</strong>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                Live DNS checks based on the selected provider ({spfProvider}).
              </div>
            </div>
            {spfProvider === 'smtp_com' && !String(smtpComDkimTxtValue || '').trim() ? (
              <Alert variant="warning" className="py-2">
                <strong>DKIM will fail until you paste the SMTP.com DKIM TXT value above.</strong> The verify check looks for
                <code className="ms-1">smtpcomcustomers._domainkey.{domain}</code>.
              </Alert>
            ) : null}
            {spfProvider === 'mailgun' && (!String(mailgunDkimSelector || '').trim() || !String(mailgunDkimTxtValue || '').trim()) ? (
              <Alert variant="warning" className="py-2">
                <strong>DKIM will fail until you enter Mailgun selector and TXT value above and Apply.</strong> Verify checks for <code>selector._domainkey.{domain}</code> (e.g. k1, mg, selector1).
              </Alert>
            ) : null}
            {spfProvider === 'mailchannels' && (!String(mailchannelsDkimSelector || '').trim() || !String(mailchannelsDkimTxtValue || '').trim()) ? (
              <Alert variant="warning" className="py-2">
                <strong>DKIM will fail until you click Generate DKIM in MailChannels and Apply.</strong> Selector is auto-generated; verify checks for <code>{normalizeMailgunSelectorInput(mailchannelsDkimSelector, domain) || 'auto-selector'}._domainkey.{domain}</code>.
              </Alert>
            ) : null}
            {spfProvider === 'elastic_email' && (!String(elasticEmailDkimSelector || '').trim() || !String(elasticEmailDkimTxtValue || '').trim()) ? (
              <Alert variant="warning" className="py-2">
                <strong>DKIM will fail until you enter Elastic Email selector and TXT value above and Apply.</strong> Verify checks for <code>{normalizeMailgunSelectorInput(elasticEmailDkimSelector || 'api', domain) || 'api'}._domainkey.{domain}</code>.
              </Alert>
            ) : null}
            {spfProvider === 'sendgrid' ? (
              <Alert variant="warning" className="py-2">
                <strong>For Twilio SendGrid, DKIM is configured in Outbound set.</strong> If DKIM is missing/failing, open
                <strong> Outbound set</strong> for this domain and update the &quot;SendGrid DNS records&quot; block.
              </Alert>
            ) : null}
            {step === 'verify' && verifyDetails && !verifyDetails.dkim?.ok && verifyDetails.dkim?.expectedName ? (
              <Alert variant="info" className="py-2 mt-2">
                <strong>DKIM not yet passing?</strong> If you just added the record, wait 1–2 minutes for DNS propagation, then click &quot;Verify now&quot; again.
              </Alert>
            ) : null}
            <Table size="sm" bordered responsive>
              <thead>
                <tr>
                  <th>Check</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>SPF</td>
                  <td>{verifyDetails?.spf?.ok ? '✅ Pass' : '⚠️ Missing/Fail'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {verifyDetails?.spf?.expectedInclude ? `expected ${verifyDetails.spf.expectedInclude}` : 'expected SPF record present'}
                  </td>
                </tr>
                <tr>
                  <td>DKIM</td>
                  <td>{verifyDetails?.dkim?.ok ? '✅ Pass' : '⚠️ Missing/Fail'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {verifyDetails?.dkim?.expectedName ? `expected ${verifyDetails.dkim.expectedName}` : 'provider DKIM not configured'}
                  </td>
                </tr>
                <tr>
                  <td>DMARC</td>
                  <td>{verifyDetails?.dmarc?.isConfigured ? '✅ Present' : '⚠️ Missing'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {verifyDetails?.dmarc?.record ? verifyDetails.dmarc.record : 'no _dmarc TXT found'}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Alert>
        ) : null}

        <div className="mt-3">
          <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 12, flexWrap: 'wrap' }}>
            <strong>DNS records to add</strong>
            {isDnsSetupLoading ? <span className="text-muted"><Spinner size="sm" animation="border" className="me-2" />Loading…</span> : null}
          </div>
          <Table size="sm" bordered responsive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {consolidatedRecords.length ? consolidatedRecords.map((r, idx) => (
                <tr key={idx}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{r.name}</td>
                  <td>{r.type}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{r.value}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={3} className="text-muted">
                    No DNS records available yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>{footer}</Modal.Footer>
    </Modal>
  );
}

function _dmarcPreviewLine(domain, value) {
  if (!domain) return '';
  return `_dmarc.${domain}  TXT  ${value}`;
}


